import React, { useState } from 'react';
import axios from 'axios';
import { Send, Phone, Mail, MapPin, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from '../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;

const FUNDING_OPTIONS = [
  'Corporate Finance',
  'Business & Working Capital',
  'Trade & Supply Chain Finance',
  'Structured Finance',
  'Real Estate Finance',
  'Asset-Backed Finance',
  'Equipment Finance',
  'Private Client / Home & Property Finance',
  'Other Capital Requirement'
];

const initialForm = { name: '', company: '', designation: '', email: '', phone: '', funding_requirement: '', amount: '', message: '' };

export const ContactSection = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/corporate-enquiry`, formData);
      toast({
        title: 'Enquiry Received',
        description: 'Our advisory team will reach out to discuss your capital requirement.',
      });
      setFormData(initialForm);
    } catch (err) {
      toast({
        title: 'Submission failed',
        description: 'Please try again or call us at +91 98927 59155.',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <section id="contact" className="py-24 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Left Side - Contact Info */}
            <div>
              <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
                Let's Discuss Your <span className="text-emerald-600">Capital Requirement</span>
              </h2>
              <p className="text-lg text-slate-600 mb-12">
                Whether the requirement involves working capital, acquisition finance, structured debt, project finance, asset-backed funding or growth capital, our team can help evaluate the appropriate financing route.
              </p>

              {/* Contact Cards */}
              <div className="space-y-6 mb-12">
                <div className="flex items-start space-x-4 p-6 bg-slate-50 rounded-xl">
                  <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900 mb-1">Call Us</h3>
                    <p className="text-slate-600">+91 98927 59155</p>
                    <p className="text-sm text-slate-500 mt-1">Mon-Sat: 9:00 AM - 7:00 PM</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4 p-6 bg-slate-50 rounded-xl">
                  <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900 mb-1">Email Us</h3>
                    <p className="text-slate-600">support@loanyardcapital.com</p>
                    <p className="text-sm text-slate-500 mt-1">We'll respond within 24 hours</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4 p-6 bg-slate-50 rounded-xl">
                  <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900 mb-1">Visit Us</h3>
                    <p className="text-slate-600">C-20, Enam Sambhav, G Block BKC<br/>Bandra Kurla Complex, Mumbai 400051</p>
                  </div>
                </div>
              </div>

              {/* Trust Badges */}
              <div className="bg-emerald-50 p-6 rounded-xl border border-emerald-200">
                <div className="flex items-center space-x-2 mb-4">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                  <span className="font-semibold text-slate-900">How your enquiry is handled</span>
                </div>
                <ul className="space-y-2 text-slate-600">
                  <li className="flex items-center space-x-2">
                    <div className="w-1.5 h-1.5 bg-emerald-600 rounded-full"></div>
                    <span>Handled confidentially by a senior advisor</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <div className="w-1.5 h-1.5 bg-emerald-600 rounded-full"></div>
                    <span>Initial feasibility assessment at no obligation</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <div className="w-1.5 h-1.5 bg-emerald-600 rounded-full"></div>
                    <span>Structures evaluated and optimized for your needs</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Right Side - Enquiry Form */}
            <Card className="border-2 border-slate-200 shadow-xl">
              <CardHeader>
                <CardTitle className="text-2xl">Discuss Your Requirement</CardTitle>
                <CardDescription>Share the requirement and our advisory team will get back to you</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="corporate-enquiry-form">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Name *</Label>
                      <Input
                        id="name"
                        data-testid="enquiry-name-input"
                        placeholder="Your full name"
                        value={formData.name}
                        onChange={(e) => handleChange('name', e.target.value)}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="company">Company</Label>
                      <Input
                        id="company"
                        data-testid="enquiry-company-input"
                        placeholder="Company / firm name"
                        value={formData.company}
                        onChange={(e) => handleChange('company', e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="designation">Designation</Label>
                      <Input
                        id="designation"
                        data-testid="enquiry-designation-input"
                        placeholder="e.g., Promoter, CFO, Director"
                        value={formData.designation}
                        onChange={(e) => handleChange('designation', e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        data-testid="enquiry-email-input"
                        type="email"
                        placeholder="your@email.com"
                        value={formData.email}
                        onChange={(e) => handleChange('email', e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone *</Label>
                      <Input
                        id="phone"
                        data-testid="enquiry-phone-input"
                        type="tel"
                        placeholder="+91 XXXXX XXXXX"
                        value={formData.phone}
                        onChange={(e) => handleChange('phone', e.target.value)}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="amount">Approximate Amount</Label>
                      <Input
                        id="amount"
                        data-testid="enquiry-amount-input"
                        type="text"
                        placeholder="e.g., ₹5 Cr"
                        value={formData.amount}
                        onChange={(e) => handleChange('amount', e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="funding_requirement">Funding Requirement *</Label>
                    <Select value={formData.funding_requirement} onValueChange={(value) => handleChange('funding_requirement', value)} required>
                      <SelectTrigger data-testid="enquiry-funding-select">
                        <SelectValue placeholder="Select funding requirement" />
                      </SelectTrigger>
                      <SelectContent>
                        {FUNDING_OPTIONS.map((opt) => (
                          <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">Message</Label>
                    <Textarea
                      id="message"
                      data-testid="enquiry-message-input"
                      placeholder="Briefly describe the requirement, purpose and timelines..."
                      value={formData.message}
                      onChange={(e) => handleChange('message', e.target.value)}
                      rows={4}
                    />
                  </div>

                  <Button type="submit" disabled={submitting} data-testid="enquiry-submit-btn" className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" size="lg">
                    {submitting ? 'Submitting...' : 'Discuss Your Requirement'}
                    <Send className="ml-2 w-4 h-4" />
                  </Button>

                  <p className="text-xs text-slate-500 text-center" data-testid="enquiry-consent-note">
                    By submitting this form you consent to being contacted by LOANYARD CAPITAL regarding your enquiry. Your information is treated confidentially and used only to evaluate this enquiry.
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};
